import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useUserActivation } from 'helpers/hooks/useUserActivation';

interface VimeoVideoProps {
  src: string;
  title: string;
  height: string;
}

const VIMEO_ORIGIN = 'https://player.vimeo.com';

/**
 * CMS authors paste either a share link (`vimeo.com/123456789/abcdef`) or the embed URL out of
 * Vimeo's snippet, whose query string arrives HTML-escaped (`&amp;`). Both are normalised to the
 * player URL so the autoplay flags can be set on it.
 *
 * `autoplay` is paired with `muted`, never used alone: no browser starts an audible video off the
 * back of a URL flag, and Vimeo answers a refused autoplay by leaving the video paused. Starting
 * muted always succeeds, and the sound is switched on straight afterwards over the player API.
 */
export const buildVimeoSrc = (rawUrl: string, autoplay: boolean) => {
  const decoded = rawUrl.replace(/&amp;/g, '&');

  try {
    const url = new URL(decoded);

    if (!url.hostname.endsWith('vimeo.com')) return decoded;

    if (url.hostname !== 'player.vimeo.com') {
      //share link: /<id> or /<id>/<privacy hash>
      const [id, privacyHash] = url.pathname.split('/').filter(Boolean);

      if (!/^\d+$/.test(id ?? '')) return decoded;

      const embedUrl = new URL(`${VIMEO_ORIGIN}/video/${id}`);
      if (privacyHash) embedUrl.searchParams.set('h', privacyHash);

      url.searchParams.forEach((value, key) => embedUrl.searchParams.set(key, value));
      url.href = embedUrl.href;
    }

    url.searchParams.set('playsinline', '1'); //iOS plays inline instead of hijacking fullscreen
    url.searchParams.set('autoplay', autoplay ? '1' : '0');
    url.searchParams.set('muted', autoplay ? '1' : '0');

    return url.toString();
  } catch {
    return decoded;
  }
};

/**
 * Embeds a Vimeo video that starts playing with sound once the visitor has interacted with the
 * site. Before that the iframe shows Vimeo's own thumbnail and play button, so a visitor who
 * landed straight on this page is left with a paused video they can start themselves.
 */
export default function VimeoVideo({ src, title, height }: VimeoVideoProps) {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const hasActivated = useUserActivation();

  //Latched, so the iframe's src is only ever rewritten once (false -> true). Re-deriving it from
  //`hasActivated` on every render would reload the player - and restart the video - the moment
  //the visitor clicks anywhere.
  const [autoplay, setAutoplay] = useState(false);
  const [isMuted, setIsMuted] = useState(false);

  //Vimeo only emits events we subscribed to, and a click inside the cross-origin iframe never
  //reaches our listeners: this is the only way to know the visitor pressed play themselves.
  const startedManuallyRef = useRef(false);

  const send = useCallback((method: string, value?: unknown) => {
    const message = value === undefined ? { method } : { method, value };
    iframeRef.current?.contentWindow?.postMessage(JSON.stringify(message), VIMEO_ORIGIN);
  }, []);

  useEffect(() => {
    if (hasActivated && !startedManuallyRef.current) setAutoplay(true);
  }, [hasActivated]);

  useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe) return;

    const handleMessage = (event: MessageEvent) => {
      if (event.origin !== VIMEO_ORIGIN) return;

      let payload = event.data;

      if (typeof payload === 'string') {
        try {
          payload = JSON.parse(payload);
        } catch {
          return;
        }
      }

      if (payload?.event === 'play') startedManuallyRef.current = true;
    };

    const subscribe = () => send('addEventListener', 'play');

    window.addEventListener('message', handleMessage);
    iframe.addEventListener('load', subscribe);
    subscribe();

    return () => {
      window.removeEventListener('message', handleMessage);
      iframe.removeEventListener('load', subscribe);
    };
  }, [send]);

  useEffect(() => {
    const iframe = iframeRef.current;
    if (!autoplay || !iframe) return;

    let soundIsOn = false;
    let playerIsMuted = true;
    let confirmedRounds = 0;
    let pollTimer: ReturnType<typeof setInterval> | undefined;
    let giveUpTimer: ReturnType<typeof setTimeout> | undefined;

    //Chrome pauses a muted autoplaying video the instant it is unmuted without permission, and it
    //does so a beat after the unmute lands - too late for a play() issued in the same breath. The
    //getters run first so each round reports how the previous round actually settled.
    const applySound = () => {
      send('getMuted');
      send('getPaused');
      send('setMuted', false);
      send('setVolume', 1);
      send('play');
    };

    const stopPolling = () => {
      clearInterval(pollTimer);
      clearTimeout(giveUpTimer);
      pollTimer = undefined;
      giveUpTimer = undefined;
    };

    //The player never announces itself and silently drops anything sent before it has booted, so
    //the unmute is re-issued until the player confirms it. If it never does, the browser refused -
    //fall back to muted playback rather than leaving the video paused by the rejected unmute.
    const startPolling = () => {
      confirmedRounds = 0;
      applySound();
      pollTimer = setInterval(applySound, 500);
      giveUpTimer = setTimeout(() => {
        stopPolling();

        if (soundIsOn) return;

        setIsMuted(true);
        send('setMuted', true);
        send('play');
      }, 8000);
    };

    const handleMessage = (event: MessageEvent) => {
      if (event.origin !== VIMEO_ORIGIN) return;

      let payload = event.data;

      if (typeof payload === 'string') {
        try {
          payload = JSON.parse(payload);
        } catch {
          return;
        }
      }

      //Replies come back as {method, value}. getMuted is asked first, so by the time getPaused
      //answers, playerIsMuted describes the same round.
      if (payload?.method === 'getMuted') {
        playerIsMuted = payload.value !== false;
        return;
      }

      if (payload?.method !== 'getPaused') return;

      //Two clean rounds, because the browser's rejection lands one beat after the unmute does.
      confirmedRounds = !playerIsMuted && payload.value === false ? confirmedRounds + 1 : 0;

      if (confirmedRounds < 2) return;

      soundIsOn = true;
      setIsMuted(false);
      stopPolling();
    };

    //A lazily loaded player can boot long after the visitor's click, so retry once it is there.
    const handleLoad = () => {
      stopPolling();
      startPolling();
    };

    window.addEventListener('message', handleMessage);
    iframe.addEventListener('load', handleLoad);
    startPolling();

    return () => {
      stopPolling();
      window.removeEventListener('message', handleMessage);
      iframe.removeEventListener('load', handleLoad);
    };
  }, [autoplay, send]);

  return (
    <div className="relative w-full">
      <iframe
        ref={iframeRef}
        src={buildVimeoSrc(src, autoplay)}
        width="100%"
        height={height}
        loading="lazy"
        frameBorder="0"
        allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media; web-share"
        referrerPolicy="strict-origin-when-cross-origin"
        allowFullScreen
        title={title}
      />
      {isMuted && (
        <button
          type="button"
          onClick={() => {
            setIsMuted(false);
            send('setMuted', false);
            send('setVolume', 1);
            send('play');
          }}
          aria-label="Unmute video"
          className="absolute right-16 top-16 rounded-full bg-black/60 px-16 py-8 text-14 font-medium text-white transition-opacity hover:bg-black/80"
        >
          Tap for sound
        </button>
      )}
    </div>
  );
}
